import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import logoLight from '../../assets/new_summit_logo/logo-light.png';
import './Onboard.css';

const CONFIG_ID = '1477231390771666';
const APP_ID = '1483694403123958';
const ONBOARD_WEBHOOK = 'https://n8n.summitvisio.com/webhook/visio-onboard';

const Onboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const sessionInfoRef = useRef(null); // holds {waba_id, phone_number_id} from the ES session-logging message
  const codeRef = useRef(null);        // holds the auth code from the FB.login callback
  const postedRef = useRef(false);     // guards so we POST the webhook exactly once

  useEffect(() => {
    if (window.FB) { initializeFB(); return; }
    window.fbAsyncInit = function () { initializeFB(); };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => setError('Failed to load Meta SDK. Please refresh.');
    document.body.appendChild(script);
  }, []);

  // Capture the Embedded Signup session-logging message (WABA ID + phone_number_id)
  useEffect(() => {
    const handler = (event) => {
      if (typeof event.origin !== 'string' || !event.origin.endsWith('facebook.com')) return;
      try {
        const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (msg && msg.type === 'WA_EMBEDDED_SIGNUP') {
          if (msg.event === 'FINISH' || msg.event === 'FINISH_ONLY_WABA') {
            sessionInfoRef.current = {
              waba_id: msg.data?.waba_id || null,
              phone_number_id: msg.data?.phone_number_id || null,
            };
            maybeFinish(); // session just arrived — post now if the code is already in hand
          } else if (msg.event === 'CANCEL' || msg.event === 'ERROR') {
            sessionInfoRef.current = null;
          }
        }
      } catch (_) { /* not an ES message */ }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
    // maybeFinish/sendCodeToWebhook are stable (ref-based); listener is mount-only on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeFB = () => {
    try {
      window.FB.init({ appId: APP_ID, cookie: true, xfbml: true, version: 'v21.0' });
      setSdkLoaded(true);
    } catch (err) {
      setError('Failed to initialize Meta SDK');
    }
  };

  // Meta sends the auth code (FB.login callback) and the session info (WABA +
  // phone number, via postMessage) as two separate async events, in either
  // order. Post to the webhook only once BOTH are in hand. `force` posts anyway
  // after a grace period, so a missing session message never leaves it hanging.
  const maybeFinish = (force = false) => {
    if (postedRef.current) return;
    const code = codeRef.current;
    const haveSession = !!(sessionInfoRef.current && sessionInfoRef.current.waba_id);
    if (code && (haveSession || force)) {
      postedRef.current = true;
      sendCodeToWebhook(code);
    }
  };

  const launchWhatsAppSignup = () => {
    if (!window.FB || !sdkLoaded) { setError('Meta SDK not ready. Please refresh.'); return; }
    setIsLoading(true);
    setError('');
    // reset for a clean (re)attempt
    postedRef.current = false;
    codeRef.current = null;
    sessionInfoRef.current = null;
    window.FB.login(
      (response) => {
        if (response.authResponse) {
          codeRef.current = response.authResponse.code;
          maybeFinish();                             // post now if the session already arrived
          setTimeout(() => maybeFinish(true), 4000); // fallback: post anyway after 4s
        } else {
          setError('WhatsApp connection was cancelled. Please try again.');
          setIsLoading(false);
        }
      },
      {
        config_id: CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' },
      }
    );
  };

  const sendCodeToWebhook = async (code) => {
    try {
      const response = await axios.post(
        ONBOARD_WEBHOOK,
        { code, session: sessionInfoRef.current, redirect_uri: `${window.location.origin}/onboard`, timestamp: new Date().toISOString() },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );
      if (response.status === 200) { setIsSuccess(true); setIsLoading(false); }
      else { setError('Failed to complete WhatsApp setup. Please try again.'); setIsLoading(false); }
    } catch (err) {
      setError('Failed to complete setup. Please try again.');
      setIsLoading(false);
    }
  };

  const resetState = () => { setIsLoading(false); setIsSuccess(false); setError(''); };

  /* ── Success screen ── */
  if (isSuccess) {
    return (
      <div className="ob-page">
        <nav className="ob-nav">
          <img src={logoLight} alt="Summit Visio" className="ob-nav__logo" />
        </nav>
        <div className="ob-success">
          <div className="ob-success__ring">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="#25D366" fillOpacity="0.1"/>
              <circle cx="32" cy="32" r="22" fill="#25D366" fillOpacity="0.15"/>
              <path d="M20 33L28 41L44 23" stroke="#25D366" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="ob-success__title">You're live!</h1>
          <p className="ob-success__body">Your WhatsApp Business Account is connected to Visio. Our team will be in touch within 24 hours to complete your onboarding.</p>
          <a href="https://summitvisio.com" className="ob-cta ob-cta--green">Back to Summit Visio</a>
        </div>
      </div>
    );
  }

  /* ── Main page ── */
  return (
    <div className="ob-page">

      {/* ── Nav ── */}
      <nav className="ob-nav">
        <img src={logoLight} alt="Summit Visio" className="ob-nav__logo" />
        <span className="ob-verified-badge">
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M5.5 1L6.8 3.7L9.7 4.14L7.6 6.19L8.09 9.08L5.5 7.7L2.91 9.08L3.4 6.19L1.3 4.14L4.2 3.7L5.5 1Z" fill="#25D366"/>
          </svg>
          Meta-Verified Tech Provider
        </span>
      </nav>

      {/* ── Hero ── */}
      <main className="ob-hero">

        {/* Left */}
        <div className="ob-hero__copy">
          <div className="ob-tag">WhatsApp Business API</div>

          <h1 className="ob-headline">
            Your business,<br/>
            <em className="ob-headline__em">always on.</em>
          </h1>

          <p className="ob-lead">
            Connect your WhatsApp Business Account and let Visio's AI agent handle your customers — 24 hours a day, 7 days a week.
          </p>

          <ul className="ob-checklist">
            {[
              ['Instant AI responses', 'No missed messages, no delays — ever'],
              ['Automated lead scoring', 'Know who is hot before you call them'],
              ['Calendar booking', 'Customers book appointments without ringing you'],
              ['Works while you sleep', '24/7 coverage with zero extra staff'],
            ].map(([title, desc]) => (
              <li key={title} className="ob-checklist__item">
                <span className="ob-checklist__tick">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2.5 7.5L6 11L11.5 4" stroke="#25D366" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <div>
                  <strong>{title}</strong>
                  <span>{desc}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="ob-pills">
            <span className="ob-pill">Setup in under 5 min</span>
            <span className="ob-pill">No technical skills needed</span>
            <span className="ob-pill">Cancel anytime</span>
          </div>
        </div>

        {/* Right — Connect panel */}
        <div className="ob-hero__panel">
          <div className="ob-panel">

            <div className="ob-panel__top">
              <div className="ob-panel__wa">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="16" fill="#25D366"/>
                  <path d="M22 18.5c-.25-.12-1.47-.72-1.7-.8-.23-.08-.4-.12-.56.12-.17.25-.64.8-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-.12-.76 1-1.25 2.22-1.42 2.43-.17.22-.34.24-.59.07-.25-.17-1.06-.39-2.02-1.24-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.44.12-.14.16-.25.25-.42.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.42H10.5c-.15 0-.38.06-.58.29-.2.23-.76.74-.76 1.81s.78 2.1.89 2.24c.11.15 1.53 2.33 3.71 3.27 2.18.94 2.18.63 2.57.59.39-.04 1.25-.51 1.43-1 .18-.49.18-.91.12-1-.06-.08-.21-.13-.43-.25z" fill="white"/>
                </svg>
              </div>
              <div>
                <h2 className="ob-panel__title">Connect WhatsApp Business</h2>
                <p className="ob-panel__sub">3 steps · under 5 minutes</p>
              </div>
            </div>

            <div className="ob-steps">
              <div className="ob-step">
                <div className="ob-step__num">1</div>
                <div className="ob-step__track"><div className="ob-step__line"></div></div>
                <p className="ob-step__label">Log in with your Facebook Business account</p>
              </div>
              <div className="ob-step">
                <div className="ob-step__num">2</div>
                <div className="ob-step__track"><div className="ob-step__line"></div></div>
                <p className="ob-step__label">Select or create your WhatsApp Business account</p>
              </div>
              <div className="ob-step">
                <div className="ob-step__num ob-step__num--last">3</div>
                <div className="ob-step__track"></div>
                <p className="ob-step__label">Verify your phone number — you're live</p>
              </div>
            </div>

            <button
              onClick={launchWhatsAppSignup}
              disabled={isLoading || !sdkLoaded}
              className="ob-cta ob-cta--green ob-cta--full"
            >
              {isLoading ? (
                <><span className="ob-spinner"></span>Connecting...</>
              ) : !sdkLoaded ? (
                'Loading...'
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 1C5.03 1 1 5.03 1 10c0 1.57.41 3.04 1.13 4.32L1 19l4.82-1.1A9 9 0 1010 1z" fill="white" fillOpacity=".3"/>
                    <path d="M13.5 11.5c-.2-.1-1.2-.6-1.38-.66-.18-.07-.32-.1-.45.1-.13.2-.52.66-.64.8-.12.13-.23.15-.44.05-.21-.1-.86-.32-1.64-1-.6-.54-1.01-1.2-1.13-1.41-.12-.2-.01-.32.09-.42.09-.09.2-.23.3-.35.1-.12.14-.2.2-.34.07-.13.03-.25-.01-.35-.05-.1-.45-1.1-.62-1.51-.16-.4-.33-.34-.45-.35H7.5c-.12 0-.31.05-.47.24-.16.18-.62.6-.62 1.45 0 .85.63 1.67.72 1.78.09.12 1.23 1.88 2.98 2.63 1.76.76 1.76.5 2.08.47.32-.03 1.04-.42 1.18-.83.14-.4.14-.75.1-.83-.04-.07-.16-.11-.34-.2z" fill="white"/>
                  </svg>
                  Connect WhatsApp Business
                </>
              )}
            </button>

            {error && (
              <div className="ob-error">
                <p>{error}</p>
                <button onClick={resetState} className="ob-cta ob-cta--ghost">Try Again</button>
              </div>
            )}

            <div className="ob-panel__footer">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M6.5 1.5L7.8 4.2L10.7 4.64L8.6 6.69L9.09 9.58L6.5 8.2L3.91 9.58L4.4 6.69L2.3 4.64L5.2 4.2L6.5 1.5Z" fill="#1877F2"/>
              </svg>
              Secured by Meta's official Embedded Signup
            </div>

            <p className="ob-panel__legal">
              By connecting you agree to our <a href="/terms-of-service">Terms</a> and <a href="/privacy-policy">Privacy Policy</a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="ob-footer">
        <span>© 2026 Summit Visio (Pty) Ltd</span>
        <span>·</span>
        <a href="https://summitvisio.com">summitvisio.com</a>
        <span>·</span>
        <a href="/privacy-policy">Privacy</a>
        <span>·</span>
        <a href="/terms-of-service">Terms</a>
      </footer>
    </div>
  );
};

export default Onboard;